// Country / state lookups for the Mail Order Shipping Zones editor.
// Backend persists ISO-3166 alpha-2 country codes ("CA" / "US") and the local
// 2-letter state/province code (e.g. "BC", "CA"). UI dropdowns show the
// human-readable name and bind to the code.

export type ShippingCountryCode = 'CA' | 'US'

export interface ShippingStateOption {
  code: string
  name: string
}

export const SHIPPING_COUNTRIES: Record<ShippingCountryCode, string> = {
  CA: 'Canada',
  US: 'United States (US)',
}

export const SHIPPING_STATES: Record<
  ShippingCountryCode,
  ShippingStateOption[]
> = {
  CA: [
    { code: 'AB', name: 'Alberta' },
    { code: 'BC', name: 'British Columbia' },
    { code: 'MB', name: 'Manitoba' },
    { code: 'NB', name: 'New Brunswick' },
    { code: 'NL', name: 'Newfoundland and Labrador' },
    { code: 'NT', name: 'Northwest Territories' },
    { code: 'NS', name: 'Nova Scotia' },
    { code: 'NU', name: 'Nunavut' },
    { code: 'ON', name: 'Ontario' },
    { code: 'PE', name: 'Prince Edward Island' },
    { code: 'QC', name: 'Quebec' },
    { code: 'SK', name: 'Saskatchewan' },
    { code: 'YT', name: 'Yukon Territory' },
  ],
  US: [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'DC', name: 'District Of Columbia' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
    { code: 'AA', name: 'Armed Forces (AA)' },
    { code: 'AE', name: 'Armed Forces (AE)' },
    { code: 'AP', name: 'Armed Forces (AP)' },
  ],
}

export const SHIPPING_COUNTRY_CODES = Object.keys(
  SHIPPING_COUNTRIES,
) as ShippingCountryCode[]

function isShippingCountryCode(value: string): value is ShippingCountryCode {
  return value === 'CA' || value === 'US'
}

export function getCountryName(code: string): string {
  return isShippingCountryCode(code) ? SHIPPING_COUNTRIES[code] : code
}

export function getStatesFor(country: string): ShippingStateOption[] {
  return isShippingCountryCode(country) ? SHIPPING_STATES[country] : []
}

export function getStateName(country: string, code: string): string {
  const match = getStatesFor(country).find((s) => s.code === code)
  return match?.name ?? code
}

// Label switches between "State" and "Province" based on country, mirroring
// the design at docs/pantry/2026.06.10/breadstack-settings.html line 4869.
export function getStateLabel(country: string): 'State' | 'Province' {
  return country === 'US' ? 'State' : 'Province'
}
